/**
 * MH-04 Biểu mẫu khai báo và sửa thiết bị — bốn nhóm trường theo Bảng 4.3.
 *
 * Nhóm KẾT NỐI là nhóm có logic phụ thuộc:
 *   * chọn GIÁN TIẾP  → bộ tương thích trở thành bắt buộc, và trường mã tham
 *     chiếu thiết bị bên hệ thống nhà cung cấp XUẤT HIỆN;
 *   * chọn TRỰC TIẾP  → hai trường đó ẨN, thay bằng ghi chú về thông tin xác thực
 *     và danh sách topic (MH-38, đợt sau).
 *
 * Kiểm tra dữ liệu hai lớp: tại trình duyệt khi rời trường, và tại máy chủ khi
 * gửi. Lỗi từ máy chủ được gắn về ĐÚNG TRƯỜNG theo tên trường trong phản hồi,
 * không hiển thị chung ở đầu biểu mẫu.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { LoiApi, thietBiApi, type ChiTietThietBi, type NutDiaBan } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi } from '../thanh-phan/TrangThaiManHinh';
import { HopXacNhan } from '../thanh-phan/HopXacNhan';

type Truong =
  | 'cumLoaId'
  | 'tenThietBi'
  | 'tenLoaiThietBi'
  | 'loaiThietBi'
  | 'nguonId'
  | 'maThon'
  | 'viDo'
  | 'kinhDo'
  | 'maNhaCungCap'
  | 'tenNhaSanXuat'
  | 'phuongThucKetNoi'
  | 'adapterId'
  | 'externalDeviceRef'
  | 'diaChiMac'
  | 'diaChiIp'
  | 'sim'
  | 'trangThai';

type DuLieuBieuMau = Record<Truong, string>;

const RONG: DuLieuBieuMau = {
  cumLoaId: '',
  tenThietBi: '',
  tenLoaiThietBi: '',
  loaiThietBi: 'cum_thu_phat_thanh',
  nguonId: '',
  maThon: '',
  viDo: '',
  kinhDo: '',
  maNhaCungCap: '',
  tenNhaSanXuat: '',
  phuongThucKetNoi: 'truc_tiep',
  adapterId: '',
  externalDeviceRef: '',
  diaChiMac: '',
  diaChiIp: '',
  sim: '',
  trangThai: 'active',
};

/** Kiểm tra tại trình duyệt, lớp thứ nhất. Trả về lỗi theo tên trường. */
export function kiemTraTaiTrinhDuyet(d: DuLieuBieuMau): Partial<Record<Truong, string>> {
  const loi: Partial<Record<Truong, string>> = {};
  if (!d.cumLoaId.trim()) loi.cumLoaId = vi.bieuMau.loiTruongBatBuoc;
  if (!d.tenThietBi.trim()) loi.tenThietBi = vi.bieuMau.loiTruongBatBuoc;
  if (!d.nguonId.trim()) loi.nguonId = vi.bieuMau.loiTruongBatBuoc;
  else if (!/^H[0-9]{2}(\.[0-9]{3})?$/.test(d.nguonId.trim())) loi.nguonId = vi.bieuMau.loiMaDiaBan;
  if (!d.maNhaCungCap.trim()) loi.maNhaCungCap = vi.bieuMau.loiTruongBatBuoc;
  if (d.phuongThucKetNoi === 'gian_tiep') {
    if (!d.adapterId) loi.adapterId = vi.bieuMau.loiTruongBatBuoc;
    if (!d.externalDeviceRef.trim()) loi.externalDeviceRef = vi.bieuMau.loiTruongBatBuoc;
  }
  return loi;
}

export function MH04BieuMauThietBi(): ReactNode {
  const { id } = useParams();
  const laSua = Boolean(id);
  const dieuHuong = useNavigate();
  const [d, datD] = useState<DuLieuBieuMau>(RONG);
  const [loi, datLoi] = useState<Partial<Record<Truong, string>>>({});
  const [daSua, datDaSua] = useState<Set<Truong>>(new Set());
  const [dangLuu, datDangLuu] = useState(false);
  const [hoiRoiTrang, datHoiRoiTrang] = useState(false);
  const [khoaMa, datKhoaMa] = useState(false);

  const nap = dungTaiDuLieu<{
    tb: ChiTietThietBi | null;
    ncc: { maNhaCungCap: string; ten: string }[];
    btt: { id: string; maNhaCungCap: string; tenNhaCungCap: string }[];
    xa: NutDiaBan[];
  }>({
    tai: async (signal) => {
      const [ncc, btt, xa, tb] = await Promise.all([
        thietBiApi.nhaCungCap(signal),
        thietBiApi.boTuongThich(signal),
        thietBiApi.cayDiaBan({}, signal),
        id ? thietBiApi.chiTiet(id, signal) : Promise.resolve(null),
      ]);
      return { tb, ncc, btt, xa };
    },
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [id],
  });

  useEffect(() => {
    const tb = nap.duLieu?.tb;
    if (!tb) return;
    const ct = tb.chiTiet as Record<string, unknown>;
    datD({
      cumLoaId: tb.cumLoaId,
      tenThietBi: tb.tenThietBi ?? '',
      tenLoaiThietBi: String(ct.tenLoaiThietBi ?? ''),
      loaiThietBi: tb.loaiThietBi,
      nguonId: tb.nguonId,
      maThon: tb.maThon ?? '',
      viDo: tb.viDo === null ? '' : String(tb.viDo),
      kinhDo: tb.kinhDo === null ? '' : String(tb.kinhDo),
      maNhaCungCap: tb.maNhaCungCap,
      tenNhaSanXuat: String(ct.tenNhaSanXuat ?? ''),
      phuongThucKetNoi: tb.phuongThucKetNoi,
      adapterId: tb.adapterId ?? '',
      externalDeviceRef: String(ct.externalDeviceRef ?? ''),
      diaChiMac: String(ct.diaChiMac ?? ''),
      diaChiIp: String(ct.diaChiIp ?? ''),
      sim: String(ct.sim ?? ''),
      trangThai: tb.trangThai,
    });
    // Mã thiết bị không sửa được sau khi thiết bị đã gửi gói tin đầu tiên.
    datKhoaMa(tb.capNhatLuc !== null);
  }, [nap.duLieu]);

  const dat = (t: Truong, v: string): void => {
    datD((cu) => ({ ...cu, [t]: v }));
    datDaSua((cu) => new Set(cu).add(t));
  };

  /** Lớp kiểm tra thứ nhất chạy khi RỜI trường, không chạy theo từng ký tự. */
  const roiTruong = (t: Truong): void => {
    const kt = kiemTraTaiTrinhDuyet(d);
    datLoi((cu) => ({ ...cu, [t]: kt[t] }));
  };

  const luu = async (): Promise<void> => {
    const kt = kiemTraTaiTrinhDuyet(d);
    if (Object.keys(kt).length > 0) {
      datLoi(kt);
      // Đưa con trỏ vào trường đầu tiên còn lỗi, không hiển thị hộp thoại.
      const dau = Object.keys(kt)[0];
      document.getElementById(`bm-${dau}`)?.focus();
      return;
    }
    datDangLuu(true);
    datLoi({});
    try {
      const than: Record<string, unknown> = {
        ...d,
        viDo: d.viDo === '' ? null : Number(d.viDo),
        kinhDo: d.kinhDo === '' ? null : Number(d.kinhDo),
        adapterId: d.phuongThucKetNoi === 'gian_tiep' ? d.adapterId : null,
        externalDeviceRef: d.phuongThucKetNoi === 'gian_tiep' ? d.externalDeviceRef : null,
      };
      const kq = laSua && id ? await thietBiApi.sua(id, than) : await thietBiApi.tao(than);
      datDaSua(new Set());
      dieuHuong(`/thiet-bi/${kq.id}`);
    } catch (e) {
      // Lỗi máy chủ gắn về đúng trường: thông báo có dạng 'Trường "x" ...'.
      if (e instanceof LoiApi) {
        const khop = /"([A-Za-z]+)"/.exec(e.message);
        const ten = khop?.[1] as Truong | undefined;
        if (ten && ten in RONG) datLoi({ [ten]: e.message } as Partial<Record<Truong, string>>);
        else datLoi({ cumLoaId: e.message });
      }
    } finally {
      datDangLuu(false);
    }
  };

  if (nap.trangThai === 'dang-tai') return <KhungXuong soDong={8} soCot={2} taiLau={nap.taiLau} />;
  if (nap.trangThai === 'loi' && nap.loi) return <TrangThaiLoi loi={nap.loi} onThuLai={nap.taiLai} />;

  const bttCuaNcc = (nap.duLieu?.btt ?? []).filter(
    (b) => !d.maNhaCungCap || b.maNhaCungCap === d.maNhaCungCap,
  );
  const xa = (nap.duLieu?.xa ?? []).filter((x) => x.cap === 'xa');
  const thon = (nap.duLieu?.xa ?? []).filter((x) => x.cap === 'thon' && x.maCha === d.nguonId);

  const oTruong = (t: Truong, nhan: string, them?: { batBuoc?: boolean; kieu?: string }): ReactNode => (
    <p className="truong">
      <label htmlFor={`bm-${t}`}>
        {nhan}
        {them?.batBuoc ? <span className="truong__bat-buoc"> ({vi.bieuMau.batBuoc})</span> : null}
      </label>
      <input
        id={`bm-${t}`}
        type={them?.kieu ?? 'text'}
        value={d[t]}
        disabled={t === 'cumLoaId' && khoaMa}
        aria-invalid={loi[t] ? true : undefined}
        aria-describedby={loi[t] ? `loi-${t}` : undefined}
        onChange={(e) => dat(t, e.target.value)}
        onBlur={() => roiTruong(t)}
      />
      {loi[t] ? (
        <span className="truong__loi" id={`loi-${t}`} role="alert">
          {loi[t]}
        </span>
      ) : null}
      {t === 'cumLoaId' && khoaMa ? (
        <span className="truong__mo-ta">{vi.bieuMau.khongSuaMa}</span>
      ) : null}
    </p>
  );

  return (
    <section className="man-hinh" data-man-hinh="MH-04">
      <nav className="vet-duong-dan" aria-label={vi.dieuHuong.thietBi}>
        <Link to="/thiet-bi">{vi.dieuHuong.thietBi}</Link> /{' '}
        <span>{laSua ? vi.bieuMau.tieuDeSua : vi.bieuMau.tieuDeTao}</span>
      </nav>
      <h1>{laSua ? vi.bieuMau.tieuDeSua : vi.bieuMau.tieuDeTao}</h1>

      <form
        className="bieu-mau"
        onSubmit={(e) => {
          e.preventDefault();
          void luu();
        }}
        noValidate
      >
        <fieldset>
          <legend>{vi.bieuMau.nhom.dinhDanh}</legend>
          {oTruong('cumLoaId', vi.bieuMau.truong.cumLoaId, { batBuoc: true })}
          {oTruong('tenThietBi', vi.bieuMau.truong.tenThietBi, { batBuoc: true })}
          <p className="truong">
            <label htmlFor="bm-loaiThietBi">{vi.bieuMau.truong.loaiThietBi}</label>
            <select
              id="bm-loaiThietBi"
              value={d.loaiThietBi}
              onChange={(e) => dat('loaiThietBi', e.target.value)}
            >
              <option value="cum_thu_phat_thanh">Cụm thu phát thanh</option>
              <option value="bang_tin_dien_tu">Bảng tin điện tử</option>
            </select>
          </p>
        </fieldset>

        <fieldset>
          <legend>{vi.bieuMau.nhom.diaBan}</legend>
          <p className="truong">
            <label htmlFor="bm-nguonId">
              {vi.bieuMau.truong.nguonId}
              <span className="truong__bat-buoc"> ({vi.bieuMau.batBuoc})</span>
            </label>
            {/* Danh sách xã lấy từ danh mục hành chính, không nhập tay, để bảo
                đảm mã định danh đúng quy định. */}
            <select
              id="bm-nguonId"
              value={d.nguonId}
              aria-invalid={loi.nguonId ? true : undefined}
              aria-describedby={loi.nguonId ? 'loi-nguonId' : undefined}
              onChange={(e) => dat('nguonId', e.target.value)}
              onBlur={() => roiTruong('nguonId')}
            >
              <option value="">{vi.bieuMau.chon}</option>
              {xa.map((x) => (
                <option key={x.maDinhDanh} value={x.maDinhDanh}>
                  {x.ten} ({x.maDinhDanh})
                </option>
              ))}
            </select>
            {loi.nguonId ? (
              <span className="truong__loi" id="loi-nguonId" role="alert">
                {loi.nguonId}
              </span>
            ) : null}
          </p>
          <p className="truong">
            <label htmlFor="bm-maThon">{vi.bieuMau.truong.maThon}</label>
            <select id="bm-maThon" value={d.maThon} onChange={(e) => dat('maThon', e.target.value)}>
              <option value="">{vi.bieuMau.chon}</option>
              {thon.map((x) => (
                <option key={x.maDinhDanh} value={x.maDinhDanh}>
                  {x.ten}
                </option>
              ))}
            </select>
          </p>
          {oTruong('viDo', vi.bieuMau.truong.viDo, { kieu: 'number' })}
          {oTruong('kinhDo', vi.bieuMau.truong.kinhDo, { kieu: 'number' })}
        </fieldset>

        <fieldset data-nhom="ket-noi">
          <legend>{vi.bieuMau.nhom.ketNoi}</legend>
          <p className="truong">
            <label htmlFor="bm-maNhaCungCap">
              {vi.bieuMau.truong.maNhaCungCap}
              <span className="truong__bat-buoc"> ({vi.bieuMau.batBuoc})</span>
            </label>
            <select
              id="bm-maNhaCungCap"
              value={d.maNhaCungCap}
              aria-invalid={loi.maNhaCungCap ? true : undefined}
              onChange={(e) => dat('maNhaCungCap', e.target.value)}
              onBlur={() => roiTruong('maNhaCungCap')}
            >
              <option value="">{vi.bieuMau.chon}</option>
              {(nap.duLieu?.ncc ?? []).map((n) => (
                <option key={n.maNhaCungCap} value={n.maNhaCungCap}>
                  {n.ten}
                </option>
              ))}
            </select>
            {loi.maNhaCungCap ? (
              <span className="truong__loi" role="alert">
                {loi.maNhaCungCap}
              </span>
            ) : null}
          </p>
          {oTruong('tenNhaSanXuat', vi.bieuMau.truong.tenNhaSanXuat)}
          <p className="truong">
            <label htmlFor="bm-phuongThucKetNoi">
              {vi.bieuMau.truong.phuongThucKetNoi}
              <span className="truong__bat-buoc"> ({vi.bieuMau.batBuoc})</span>
            </label>
            <select
              id="bm-phuongThucKetNoi"
              value={d.phuongThucKetNoi}
              onChange={(e) => dat('phuongThucKetNoi', e.target.value)}
            >
              <option value="truc_tiep">{vi.thietBi.ketNoi.truc_tiep}</option>
              <option value="gian_tiep">{vi.thietBi.ketNoi.gian_tiep}</option>
            </select>
          </p>

          {d.phuongThucKetNoi === 'gian_tiep' ? (
            <>
              <p className="truong">
                <label htmlFor="bm-adapterId">
                  {vi.bieuMau.truong.adapterId}
                  <span className="truong__bat-buoc"> ({vi.bieuMau.batBuoc})</span>
                </label>
                <select
                  id="bm-adapterId"
                  value={d.adapterId}
                  aria-invalid={loi.adapterId ? true : undefined}
                  onChange={(e) => dat('adapterId', e.target.value)}
                  onBlur={() => roiTruong('adapterId')}
                >
                  <option value="">{vi.bieuMau.chon}</option>
                  {bttCuaNcc.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.tenNhaCungCap}
                    </option>
                  ))}
                </select>
                {loi.adapterId ? (
                  <span className="truong__loi" role="alert">
                    {loi.adapterId}
                  </span>
                ) : null}
              </p>
              {oTruong('externalDeviceRef', vi.bieuMau.truong.externalDeviceRef, { batBuoc: true })}
              <p className="truong__mo-ta">{vi.bieuMau.ghiChuGianTiep}</p>
            </>
          ) : (
            <p className="truong__mo-ta" data-vung="ghi-chu-truc-tiep">
              {vi.bieuMau.ghiChuTrucTiep}
            </p>
          )}
        </fieldset>

        <fieldset>
          <legend>{vi.bieuMau.nhom.thongSo}</legend>
          {oTruong('diaChiMac', vi.bieuMau.truong.diaChiMac)}
          {oTruong('diaChiIp', vi.bieuMau.truong.diaChiIp)}
          {oTruong('sim', vi.bieuMau.truong.sim)}
          <p className="truong">
            <label htmlFor="bm-trangThai">{vi.bieuMau.truong.trangThai}</label>
            <select
              id="bm-trangThai"
              value={d.trangThai}
              onChange={(e) => dat('trangThai', e.target.value)}
            >
              <option value="active">{vi.thietBi.quanLy.active}</option>
              <option value="inactive">{vi.thietBi.quanLy.inactive}</option>
            </select>
          </p>
        </fieldset>

        <div className="bieu-mau__nut">
          <button
            type="button"
            className="nut"
            onClick={() => (daSua.size > 0 ? datHoiRoiTrang(true) : dieuHuong('/thiet-bi'))}
          >
            {vi.bieuMau.huy}
          </button>
          {/* Nút gửi chuyển sang trạng thái đang xử lý và không cho bấm lại. */}
          <button type="submit" className="nut nut--chinh" disabled={dangLuu}>
            {dangLuu ? vi.bieuMau.dangLuu : vi.bieuMau.luu}
          </button>
        </div>
      </form>

      {hoiRoiTrang ? (
        <HopXacNhan
          tieuDe={vi.bieuMau.roiVaBo}
          heQua={dienVao(vi.bieuMau.conThayDoiChuaLuu, { so: daSua.size })}
          nhanThucHien={vi.bieuMau.roiVaBo}
          onHuy={() => datHoiRoiTrang(false)}
          onThucHien={() => dieuHuong('/thiet-bi')}
        />
      ) : null}
    </section>
  );
}
